import type { ReactElement, ReactNode } from "react";

import ReactDOMServer from "react-dom/server";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { ChangeValue } from "@similarweb/ui-components";

import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import noZoomConfig from "pro-features/components/Chart/src/configs/zoom/noZoomConfig";
import { resolveHighChartLabelProps } from "pro-features/components/Chart/src/utils";
import { minVisitsAbbrFilter, yAxisChangeFilter } from "app/filters/ngFilters";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

export enum EChartDisplayType {
    ABSOLUTE,
    PERCENTAGE,
}

const TooltipContainer = styled.div`
    background-color: white;
    border-radius: 6px;
`;

const { ABSOLUTE } = EChartDisplayType;
let labels: FixmeType[] = [];
export const renderChangeLabelsHof = (labelPadding: number) => (highchartRef: FixmeType) => {
    highchartRef.series[0].points.forEach((point: FixmeType, index: number) => {
        const LABEL_Y_AXIS_PADDING = 20;
        const { clientX, options } = point;
        const { labelChange } = options;
        if (!labelChange) {
            return;
        }
        const x = clientX + labelPadding;
        const allY = highchartRef.series.map(({ points }: { points: FixmeType[] }) => points[index].shapeArgs.y);
        const y = Math.min(...allY) - LABEL_Y_AXIS_PADDING;
        const labelRef = highchartRef.renderer
            .label(
                ...resolveHighChartLabelProps({
                    displayValue: ReactDOMServer.renderToString(
                        <ChangeValue
                            value={yAxisChangeFilter()(labelChange, 2)}
                            isDecrease={labelChange < 0}
                        />,
                    ),
                    x,
                    y,
                    useHTML: true,
                }),
            )
            .add();
        labels.push(labelRef);
    });
};

export type RenderCustomLabels = (highchartRef: FixmeType) => void;

interface IColumnChartConfig {
    categories?: Array<string | ReactNode>;
    chartDisplayType?: EChartDisplayType;
    maxPointWidth?: number;
    displayDataLabelsOnColumns?: boolean;
    height?: number;
    tooltipFormatter?: (highChartRef: FixmeType) => ReactElement;
    tooltipConfig?: Highcharts.TooltipOptions;
    maxYaxisValue?: number;
    renderCustomLabels?: RenderCustomLabels;
    renderCustomCategories?: (index: number) => ReactElement | ReactNode;
    marginTop?: number;
    yAxisFormatter?: ({ value }: { value: number }) => string;
}

export const columnChartConfig = (args: IColumnChartConfig) => {
    const {
        categories,
        maxPointWidth = 48,
        chartDisplayType = ABSOLUTE,
        displayDataLabelsOnColumns = true,
        height = 297,
        tooltipFormatter,
        tooltipConfig = {},
        maxYaxisValue,
        renderCustomLabels,
        renderCustomCategories = false,
        marginTop = 10,
        yAxisFormatter,
    } = args;
    const defaultYAxisFormatter = ({ value }: { value: number }) =>
        chartDisplayType === EChartDisplayType.ABSOLUTE ? minVisitsAbbrFilter()(value) : yAxisChangeFilter()(value, 2);

    return combineConfigs({ type: [chartTypes.COLUMN], yAxisFormatter: yAxisFormatter || defaultYAxisFormatter }, [
        noLegendConfig,
        xAxisCrosshair,
        yAxisLabelsConfig,
        tooltipContainerConfig,
        noZoomConfig,
        {
            chart: {
                type: "column",
                animation: false,
                borderColor: "#ffffff",
                height,
                marginTop,
                events: {
                    load: function () {
                        if (!displayDataLabelsOnColumns) {
                            return;
                        }
                        renderCustomLabels?.(this);
                    },
                    redraw: function () {
                        if (!displayDataLabelsOnColumns) {
                            return;
                        }
                        labels.forEach((label) => label.destroy());
                        labels = [];
                        renderCustomLabels?.(this);
                    },
                },
            },
            plotOptions: {
                series: {
                    states: {
                        hover: {
                            enabled: false,
                        },
                    },
                    point: {
                        events: {
                            click: function (this: Highcharts.Point) {
                                // @ts-ignore FIXME: strict migration - onClick is a custom property on point options
                                this.options.onClick?.();
                            },
                        },
                    },
                },
                column: {
                    grouping: true,
                    maxPointWidth,
                    minPointLength: 3,
                    pointPadding: 0.1,
                    borderRadius: 4,
                },
            },
            tooltip: {
                enabled: Boolean(tooltipFormatter),
                shared: true,
                outside: false,
                useHTML: true,
                padding: 0,
                formatter() {
                    if (!tooltipFormatter) {
                        return;
                    }
                    return ReactDOMServer.renderToString(<TooltipContainer>{tooltipFormatter(this)}</TooltipContainer>);
                },
                ...tooltipConfig,
            },
            yAxis: {
                gridLineWidth: 0.5,
                showFirstLabel: true,
                showLastLabel: true,
                reversed: false,
                gridZIndex: 2,
                reversedStacks: true,
                tickPixelInterval: 65,
                labels: {
                    style: {
                        fontSize: "11px",
                        fontFamily: "Roboto",
                        color: colorsPalettes.carbon[400],
                    },
                },
                ...(maxYaxisValue && { max: maxYaxisValue }),
            },
            xAxis: {
                lineColor: colorsPalettes.carbon[50],
                gridLineWidth: 0,
                gridLineDashStyle: "dash",
                tickLength: 0,
                categories: categories?.map((category) => ReactDOMServer.renderToString(category)),
                crosshair: false,
                labels: {
                    style: {
                        fontSize: "11px",
                        color: colorsPalettes.carbon[400],
                        fontFamily: "Roboto",
                        zIndex: 0,
                    },
                    ...(renderCustomCategories && {
                        useHTML: true,
                        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                            return ReactDOMServer.renderToString(renderCustomCategories(this.pos));
                        },
                    }),
                },
                minPadding: 0,
                maxPadding: 0,
            },
        },
    ]);
};
